/* Smart chat assistant: FAQ/intent matching + live product suggestions. No external AI. */
if (!customElements.get('smart-assistant')) {
  const STORE_KEY = 'amutham-assistant-v1';
  const STOP = new Set('a an the is are am was were be to of in on for and or my me i you your we our it this that what which how do does can could would should will with at by from please pls tell about any have has get want need know show give'.split(' '));

  const lev = (a, b) => {
    if (Math.abs(a.length - b.length) > 1) return 2;
    const m = a.length, n = b.length;
    let prev = Array.from({ length: n + 1 }, (_, j) => j);
    for (let i = 1; i <= m; i++) {
      const cur = [i];
      for (let j = 1; j <= n; j++) cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = cur;
    }
    return prev[n];
  };

  customElements.define('smart-assistant', class SmartAssistant extends HTMLElement {
    connectedCallback() {
      try { this.kb = JSON.parse(this.querySelector('.sa__kb').textContent); } catch (e) { return; }
      this.launcher = this.querySelector('.sa__launcher');
      this.panel = this.querySelector('.sa__panel');
      this.launcher.addEventListener('click', () => (this.panel.hidden ? this.open() : this.close()));
      // Other entry points (mobile bar chat item, links) open the assistant instead of WhatsApp.
      document.addEventListener('click', (e) => {
        const t = e.target.closest('[data-assistant-open]');
        if (t && this.isVisibleHere()) { e.preventDefault(); this.open(); }
      });
      document.querySelectorAll('.desktop-whatsapp-widget').forEach((el) => { el.style.display = 'none'; });
      this.syn = new Map((this.kb.synonyms || []).map(([k, v]) => [k.toLowerCase(), v.toLowerCase()]));
      this.entries = (this.kb.entries || []).map((en) => ({ ...en, tokens: this.tokens(`${en.k || ''} ${en.q || ''}`), kw: (en.k || '').toLowerCase().split(',').map((x) => this.norm(x)).filter(Boolean) }));
      this.money = (() => { try { return new Intl.NumberFormat(this.kb.locale, { style: 'currency', currency: this.kb.currency }); } catch (e) { return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.kb.currency || 'INR' }); } })();
      if (sessionStorage.getItem(STORE_KEY + ':open') === '1' && window.innerWidth > 749) this.open(true);
    }

    isVisibleHere() { return getComputedStyle(this.launcher).display !== 'none' || window.innerWidth <= 749; }

    /* ---------- UI ---------- */
    build() {
      if (this.built) return;
      this.built = true;
      this.log = this.querySelector('.sa__log');
      this.chips = this.querySelector('.sa__chips');
      this.input = this.querySelector('.sa__input');
      this.querySelector('.sa__form').addEventListener('submit', (e) => { e.preventDefault(); this.ask(this.input.value); });
      this.querySelector('.sa__close').addEventListener('click', () => this.close());
      this.querySelector('.sa__reset').addEventListener('click', () => this.reset());
      this.panel.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') {
          const f = [...this.panel.querySelectorAll('button, a[href], input')].filter((x) => !x.disabled && x.offsetParent !== null);
          if (!f.length) return;
          if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
          else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
        }
      });
      this.log.addEventListener('click', (e) => this.onLogClick(e));
      this.renderChips();
      const saved = this.load();
      if (saved.length) saved.forEach((m) => this.paint(m, false));
      else this.push({ from: 'bot', text: this.kb.greeting });
    }

    open(silent) {
      this.build();
      this.panel.hidden = false;
      this.classList.add('is-open');
      this.launcher.setAttribute('aria-expanded', 'true');
      if (window.innerWidth <= 749) document.documentElement.classList.add('sa-lock');
      sessionStorage.setItem(STORE_KEY + ':open', '1');
      this.scrollEnd();
      if (!silent) requestAnimationFrame(() => this.input.focus({ preventScroll: true }));
    }

    close() {
      this.panel.hidden = true;
      this.classList.remove('is-open');
      this.launcher.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('sa-lock');
      sessionStorage.setItem(STORE_KEY + ':open', '0');
      this.launcher.focus({ preventScroll: true });
    }

    reset() {
      sessionStorage.removeItem(STORE_KEY);
      this.log.innerHTML = '';
      this.push({ from: 'bot', text: this.kb.greeting });
      this.renderChips();
      this.input.focus();
    }

    renderChips(list) {
      const items = list || this.kb.quick || [];
      this.chips.innerHTML = '';
      items.forEach((q) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'sa__chip';
        b.textContent = q.label;
        b.addEventListener('click', () => this.ask(q.text, q.label));
        this.chips.appendChild(b);
      });
      this.chips.hidden = !items.length;
    }

    esc(s) { const d = document.createElement('div'); d.textContent = s == null ? '' : String(s); return d.innerHTML; }

    load() { try { return JSON.parse(sessionStorage.getItem(STORE_KEY) || '[]'); } catch (e) { return []; } }

    save(msg) {
      const all = this.load();
      all.push(msg);
      try { sessionStorage.setItem(STORE_KEY, JSON.stringify(all.slice(-40))); } catch (e) { /* storage full or blocked */ }
    }

    push(msg) { this.save(msg); this.paint(msg, true); }

    paint(msg, animate) {
      const row = document.createElement('div');
      row.className = `sa__msg sa__msg--${msg.from}${animate ? ' sa__msg--in' : ''}`;
      let html = '';
      if (msg.text) html += `<div class="sa__bubble">${msg.from === 'bot' && msg.html ? msg.text : this.esc(msg.text)}</div>`;
      if (msg.products && msg.products.length) {
        html += '<div class="sa__products">' + msg.products.map((p) => `
          <div class="sa__card">
            <a class="sa__card-img" href="${this.esc(p.url)}" tabindex="-1" aria-hidden="true">${p.img ? `<img src="${this.esc(p.img)}" alt="" width="64" height="64" loading="lazy">` : ''}</a>
            <div class="sa__card-body">
              <a class="sa__card-title" href="${this.esc(p.url)}">${this.esc(p.title)}</a>
              <span class="sa__card-price">${this.esc(p.price)}</span>
              <div class="sa__card-actions">
                ${p.available ? `<button type="button" class="sa__btn sa__btn--add" data-handle="${this.esc(p.handle)}">${this.esc(this.kb.addLabel)}</button>` : `<span class="sa__soldout">${this.esc(this.kb.soldOutLabel)}</span>`}
                <a class="sa__btn sa__btn--ghost" href="${this.esc(p.url)}">${this.esc(this.kb.viewLabel)}</a>
              </div>
            </div>
          </div>`).join('') + '</div>';
      }
      if (msg.links && msg.links.length) {
        html += '<div class="sa__links">' + msg.links.map((l) => `<a class="sa__link${l.wa ? ' sa__link--wa' : ''}" href="${this.esc(l.url)}"${l.wa ? ' target="_blank" rel="noopener"' : ''}>${this.esc(l.label)}</a>`).join('') + '</div>';
      }
      row.innerHTML = html;
      this.log.appendChild(row);
      this.scrollEnd();
    }

    scrollEnd() { if (this.log) this.log.scrollTop = this.log.scrollHeight; }

    typing(on) {
      if (on) {
        this.typer = document.createElement('div');
        this.typer.className = 'sa__msg sa__msg--bot sa__typing';
        this.typer.innerHTML = `<div class="sa__bubble"><span></span><span></span><span></span><em class="visually-hidden">${this.esc(this.kb.typingLabel)}</em></div>`;
        this.log.appendChild(this.typer);
        this.scrollEnd();
      } else if (this.typer) { this.typer.remove(); this.typer = null; }
    }

    async onLogClick(e) {
      const add = e.target.closest('.sa__btn--add');
      if (!add || add.disabled) return;
      add.disabled = true;
      const label = add.textContent;
      try {
        const prod = await (await fetch(`${this.kb.root.replace(/\/$/, '')}/products/${encodeURIComponent(add.dataset.handle)}.js`)).json();
        const variant = (prod.variants || []).find((v) => v.available) || prod.variants[0];
        const res = await fetch(`${this.kb.root.replace(/\/$/, '')}/cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }) });
        if (!res.ok) throw new Error('add failed');
        add.textContent = this.kb.addedLabel;
        add.classList.add('is-done');
        const cart = await (await fetch(`${this.kb.root.replace(/\/$/, '')}/cart.js`)).json();
        document.querySelectorAll('.header__actions_btn_cart_num:not(.wishlist__count):not(.compare__count), .cart-count-bubble').forEach((el) => {
          const inner = el.querySelector('span[aria-hidden="true"]');
          (inner || el).textContent = cart.item_count;
        });
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart } }));
      } catch (err) {
        add.textContent = this.kb.errorLabel;
        setTimeout(() => { add.textContent = label; add.disabled = false; }, 2200);
      }
    }

    /* ---------- Brain ---------- */
    norm(s) {
      return String(s || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim()
        .split(' ').map((w) => this.syn.get(w) || w).join(' ');
    }

    tokens(s) { return this.norm(s).split(' ').filter((w) => w && (w.length > 2 || /\d/.test(w)) && !STOP.has(w)); }

    score(q, en) {
      const qn = ` ${this.norm(q)} `;
      let sc = 0;
      // Exact keyword / phrase hits weigh most.
      en.kw.forEach((k) => { if (k && qn.includes(` ${k} `)) sc += k.includes(' ') ? 3 : 2; });
      const qt = this.tokens(q);
      qt.forEach((w) => {
        if (en.tokens.includes(w)) sc += 1;
        else if (w.length >= 5 && en.tokens.some((t) => t.length >= 5 && lev(w, t) <= 1)) sc += 0.75;
      });
      return sc;
    }

    has(list, q) {
      const qn = ` ${this.norm(q)} `;
      return String(list || '').split(',').map((w) => this.norm(w)).filter(Boolean).some((w) => qn.includes(` ${w} `));
    }

    waLink(text) { return this.kb.whatsappUrl ? `${this.kb.whatsappUrl}?text=${encodeURIComponent(text)}` : null; }

    handoffLinks(q) {
      const links = [];
      const wa = this.waLink(q);
      if (wa) links.push({ label: this.kb.whatsappLabel, url: wa, wa: true });
      if (this.kb.contactUrl) links.push({ label: this.kb.contactLabel, url: this.kb.contactUrl });
      return links;
    }

    async ask(raw, shown) {
      const q = String(raw || '').trim();
      if (!q || this.busy) return;
      this.busy = true;
      this.input.value = '';
      this.push({ from: 'user', text: shown || q });
      this.typing(true);
      const started = Date.now();
      let reply;
      try { reply = await this.think(q); } catch (e) { reply = { from: 'bot', text: this.kb.fallback, links: this.handoffLinks(q) }; }
      const wait = Math.max(0, (matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 450) - (Date.now() - started));
      setTimeout(() => { this.typing(false); this.push(reply); this.busy = false; }, wait);
    }

    async think(q) {
      if (q.toLowerCase() === 'whatsapp') return { from: 'bot', text: this.kb.whatsappLabel, links: this.handoffLinks('') };
      const qt = this.tokens(q);
      if (this.has(this.kb.thanksWords, q) && qt.length <= 3) return { from: 'bot', text: this.kb.thanksReply };
      if (this.has(this.kb.helloWords, q) && qt.length <= 2) return { from: 'bot', text: this.kb.helloReply };

      let best = null, bestScore = 0;
      this.entries.forEach((en) => { const sc = this.score(q, en); if (sc > bestScore) { best = en; bestScore = sc; } });

      if (best && bestScore >= (this.kb.threshold || 2)) {
        const msg = { from: 'bot', text: best.a || '', html: best.type === 'faq', links: [] };
        if (best.type === 'intent') { msg.text = this.esc(best.a || '').replace(/\n/g, '<br>'); msg.html = true; }
        if (best.col) msg.links.push({ label: best.col.t, url: best.col.u });
        if (best.bl && best.bu) msg.links.push({ label: best.bl, url: best.bu });
        if (best.wa) msg.links.push(...this.handoffLinks(q).filter((l) => l.wa));
        if (best.search) msg.products = await this.products(best.search);
        else if (best.col) msg.products = await this.collectionProducts(best.col.u);
        return msg;
      }

      // No confident answer: try products, then hand off.
      // Predictive search is fuzzy, so only keep products that share a real word with the question.
      const prods = (await this.products(q)).filter((p) => {
        const hay = this.tokens(`${p.title} ${p.tags}`);
        return qt.some((w) => hay.some((h) => h === w || (w.length >= 4 && (h.startsWith(w) || w.startsWith(h))) || (w.length >= 5 && h.length >= 5 && lev(w, h) <= 1)));
      });
      if (prods.length) return { from: 'bot', text: this.kb.productsIntro, products: prods };
      return { from: 'bot', text: this.kb.fallback, links: this.handoffLinks(q) };
    }

    async collectionProducts(url) {
      try {
        const data = await (await fetch(`${url.split('?')[0]}/products.json?limit=4`)).json();
        return (data.products || []).map((p) => {
          const v = p.variants || [];
          const prices = v.map((x) => parseFloat(x.price)).filter((x) => !isNaN(x));
          const min = Math.min(...prices), max = Math.max(...prices);
          const img = p.images && p.images[0] ? p.images[0].src : '';
          let src = img;
          try { const u = new URL(img); u.searchParams.set('width', '160'); src = u.toString(); } catch (e) { /* keep original */ }
          const price = prices.length ? (max > min ? `${this.money.format(min)} – ${this.money.format(max)}` : this.money.format(min)) : '';
          return { title: p.title, url: `${this.kb.root.replace(/\/$/, '')}/products/${p.handle}`, handle: p.handle, img: src, price, available: v.some((x) => x.available) };
        });
      } catch (e) { return []; }
    }

    async products(q) {
      const params = new URLSearchParams({ q, 'resources[type]': 'product', 'resources[limit]': '4', 'resources[options][unavailable_products]': 'last' });
      try {
        const data = await (await fetch(`${this.kb.root.replace(/\/$/, '')}/search/suggest.json?${params}`)).json();
        return (data.resources.results.products || []).map((p) => {
          const img = (p.featured_image && p.featured_image.url) || p.image;
          let src = img;
          try { const u = new URL(img, location.origin); u.searchParams.set('width', '160'); src = u.toString(); } catch (e) { /* keep original */ }
          const min = parseFloat(p.price_min || p.price), max = parseFloat(p.price_max || p.price);
          const price = isNaN(min) ? '' : (max > min ? `${this.money.format(min)} – ${this.money.format(max)}` : this.money.format(min));
          return { title: p.title, url: p.url.split('?')[0], handle: p.handle, img: src, price, available: p.available, tags: (p.tags || []).join(' ') + ' ' + (p.type || '') };
        });
      } catch (e) { return []; }
    }
  });
}
